import { useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileFilterSheetProps {
  /** Filter UI to render — same children that would normally sit inline on desktop. */
  children: ReactNode;
  /** Trigger button label. */
  triggerLabel?: string;
  /** Sheet title. */
  title?: string;
  /** Optional badge count showing how many filters are active. */
  activeCount?: number;
  /** When false (desktop), render children inline instead of behind a sheet trigger. */
  inlineOnDesktop?: boolean;
}

/**
 * Wraps a horizontal filter bar so that on mobile it collapses behind a
 * "Filters" button + bottom sheet. On desktop it renders inline by default.
 */
export function MobileFilterSheet({
  children,
  triggerLabel = 'Filters',
  title = 'Filters',
  activeCount,
  inlineOnDesktop = true,
}: MobileFilterSheetProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  if (!isMobile && inlineOnDesktop) {
    return <>{children}</>;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          {triggerLabel}
          {activeCount !== undefined && activeCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-blue px-1.5 text-xs font-medium text-white">
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 pb-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
